import React, { useEffect, useMemo, useRef, useState } from 'react'
import { buildHeaders } from './api'
import { filterLayoutToIncludedSources } from './artworkSource'
import QuoteDetails from './QuoteDetails'
import BuilderAlert from './BuilderAlert'

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const defaultRedirectTo = (url) => {
  window.location.href = url
}

const recoveryStorageKey = (vendorToken) => `gspress:${vendorToken}:last_resume_url`

function saveRecoveryUrl(vendorToken, resumeUrl) {
  if (!resumeUrl) return

  try {
    window.localStorage?.setItem(recoveryStorageKey(vendorToken), resumeUrl)
  } catch {
    // Browsers can block localStorage in private or embedded contexts.
  }
}

function clientQuoteFromResponse(quote) {
  if (!quote) return null

  return {
    hasContent: true,
    widthInches: quote.width_inches,
    heightInches: quote.height_inches,
    totalHeightInches: quote.height_inches,
    sheetCount: quote.sheet_count,
    sheetHeightsInches: quote.sheet_heights_inches,
    squareFeet: quote.square_feet,
    rateCents: quote.price_per_square_foot_cents,
    totalCents: quote.total_cents,
    billingUnit: quote.billing_unit,
    tiersVersion: quote.tiers_version ?? null,
    sheets: quote.sheets ? quote.sheets.map(s => ({ sheet: s.sheet, heightIn: s.height_in, priceCents: s.price_cents })) : null,
  }
}

export default function SummaryScreen({
  layout,
  thumbnails,
  sheetSize,
  images,
  quantity,
  artworkQualityAcknowledged = false,
  pricingMode,
  quote: quoteProp,
  vendorToken,
  builderSessionId,
  onBack,
  redirectTo = defaultRedirectTo,
  customerPortalPath,
  builderDraftPublicId,
  customerEmail,
  beforeSubmit,
  geometryEpoch = null,
  onGeometryConflict = null,
  readOnlyRecovery = false,
  recoveryCheckoutUrl = null,
  exactShopifyCheckoutAvailable = false,
  checkoutMode = 'shopify',
  vendorName = 'the print shop',
  artworkRightsPolicyVersion = '2026-08-25',
  onPricingSettingsChange,
}) {
  const [email, setEmail]             = useState(customerEmail || '')
  const [submitting, setSubmitting]   = useState(false)
  const [error, setError]             = useState(null)
  const [checkoutUrl, setCheckoutUrl] = useState(null)
  const [resumeUrl, setResumeUrl]     = useState(null)
  const [copyStatus, setCopyStatus]   = useState(null)
  const [recoveryEmailQueued, setRecoveryEmailQueued] = useState(false)
  const [savedReferenceId, setSavedReferenceId] = useState(null)
  const [authoritativeQuote, setAuthoritativeQuote] = useState(null)
  const [checkoutError, setCheckoutError] = useState(null)
  const [savedCheckoutMode, setSavedCheckoutMode] = useState(null)
  const [artworkRightsAccepted, setArtworkRightsAccepted] = useState(false)
  const [refreshedQuote, setRefreshedQuote] = useState(null)
  // The 422 review replaces the quote the parent screen is still holding —
  // the customer reviews the server's recalculated total, not the stale one.
  const quote = refreshedQuote || quoteProp
  const submitLockedRef               = useRef(false)

  // A new quote from the parent (a fresh autosave, a different draft) means
  // any stale 422 review is no longer relevant to what's on screen.
  useEffect(() => {
    setRefreshedQuote(null)
  }, [quoteProp])
  const includedLayout                = useMemo(
    () => filterLayoutToIncludedSources(layout, images),
    [layout, images]
  )
  const physicalSheetCount = includedLayout?.sheets?.filter(sheet => (
    (sheet.items || []).length > 0
  )).length || 1
  const requestedQuantity = quantity || 1
  const fixedOrderQuote = useMemo(() => {
    const sourceQuote = authoritativeQuote || quote
    const configuredPriceCents = sheetSize?.price_cents
    const totalCents = Number.isFinite(sourceQuote?.totalCents)
      ? sourceQuote.totalCents
      : Number.isFinite(configuredPriceCents)
        ? configuredPriceCents * physicalSheetCount * requestedQuantity
        : null

    if (!sourceQuote && !Number.isFinite(totalCents)) return null

    return {
      hasContent: true,
      widthInches: sourceQuote?.widthInches ?? sheetSize?.width_in,
      heightInches: sourceQuote?.heightInches ?? sheetSize?.height_in,
      totalHeightInches: (sourceQuote?.heightInches ?? sheetSize?.height_in) * physicalSheetCount,
      sheetCount: sourceQuote?.sheetCount ?? physicalSheetCount,
      squareFeet: null,
      rateCents: null,
      totalCents,
    }
  }, [authoritativeQuote, physicalSheetCount, quote, requestedQuantity, sheetSize])

  const isValidEmail = EMAIL_RE.test(email.trim())
  // Blocks submission before the customer ever hits the server: both are
  // quote shapes the backend would reject outright (MAX_VARIANT_SHEETS, or a
  // sheet taller than the tallest configured tier).
  const quoteBlockReason = quote?.tooManySheets
    ? 'Split this job into two orders (5 sheets per order).'
    : quote?.missingTier
      ? 'This sheet is taller than the largest size available.'
      : null
  const customerPortalHref = customerPortalPath || `/builder/${vendorToken}/customer`
  const activeCheckoutMode = savedCheckoutMode || checkoutMode
  const vendorManagedPayment = activeCheckoutMode === 'manual_invoice'

  useEffect(() => {
    if (customerEmail) setEmail(customerEmail)
  }, [customerEmail])

  const handleSubmit = async () => {
    if (!isValidEmail || !artworkRightsAccepted || submitLockedRef.current) return

    submitLockedRef.current = true
    setSubmitting(true)
    setError(null)
    setCheckoutError(null)
    setCopyStatus(null)

    try {
      if (beforeSubmit) {
        const canSubmit = await beforeSubmit()
        if (!canSubmit) {
          setError('This draft changed elsewhere. Reload before submitting.')
          submitLockedRef.current = false
          setSubmitting(false)
          return
        }
      }

      // What the client believes it is submitting. Absent values mean "no claim"
      // on the server, so only send what is actually known.
      //
      // Deliberately no expected_draft_lock_version: beforeSubmit above flushes
      // autosave, which bumps the server's lock_version after this component
      // captured it, so claiming it would 409 on every submission. geometry_epoch
      // does not move on autosave — only a retarget bumps it — which is exactly
      // the staleness this handshake exists to catch.
      const expectations = {}
      if (Number.isInteger(geometryEpoch)) {
        expectations.expected_geometry_epoch = geometryEpoch
      }
      if (images?.length) {
        expectations.expected_image_revisions = Object.fromEntries(
          images
            .filter(image => image?.id != null)
            .map(image => [String(image.id), Number(image.backgroundRevision ?? 0)])
        )
      }

      const response = await fetch(`/builder/${vendorToken}/gangsheets`, {
        method: 'POST',
        headers: buildHeaders(builderSessionId),
        body: JSON.stringify({
          ...expectations,
          customer_email:       email.trim(),
          vendor_sheet_size_id: sheetSize.id,
          layout_json:          includedLayout,
          image_configs:        images || [],
          preview_image:        thumbnails?.[0] || null,
          quantity:             quantity || 1,
          ...(quote?.billingUnit === 'sheet_variant'
            ? { expected_total_cents: quote.totalCents, tiers_version: quote.tiersVersion }
            : {}),
          artwork_quality_acknowledged: artworkQualityAcknowledged,
          artwork_rights_accepted: artworkRightsAccepted,
          artwork_rights_policy_version: artworkRightsPolicyVersion,
          builder_draft_public_id: builderDraftPublicId,
        }),
      })

      const data = await response.json()

      // A 409 here means the server retargeted the artwork underneath this
      // screen. That is recoverable: adopt the authoritative geometry and let
      // the customer resubmit, rather than showing a dead-end error.
      if (response.status === 409 && onGeometryConflict) {
        await onGeometryConflict()
        setError('Your artwork was updated. Check the layout and submit again.')
        submitLockedRef.current = false
        setSubmitting(false)
        return
      }

      // The server's quote (and the tier table it was built from) moved since
      // this screen last fetched it — show the server's own recalculated
      // total rather than a client-side recompute, so the number on screen
      // (and the expected_total_cents/tiers_version the next submit sends)
      // is exactly what the shop will charge.
      if (response.status === 422 && data.pricing_settings && data.quote) {
        onPricingSettingsChange?.(data.pricing_settings)
        setRefreshedQuote(clientQuoteFromResponse(data.quote))
        setError(data.error || 'The price was updated. Review the new total and submit again.')
        submitLockedRef.current = false
        setSubmitting(false)
        return
      }

      if (!response.ok) {
        setError(data.error || 'Something went wrong. Please try again.')
        submitLockedRef.current = false
        setSubmitting(false)
        return
      }

      if (data.resume_url) {
        setResumeUrl(data.resume_url)
        saveRecoveryUrl(vendorToken, data.resume_url)
      }

      setRecoveryEmailQueued(data.recovery_email_queued === true)
      setSavedReferenceId(data.reference_id)
      setSavedCheckoutMode(data.checkout_mode || checkoutMode)
      setAuthoritativeQuote(clientQuoteFromResponse(data.quote))
      setCheckoutUrl(data.redirect_url)
      setCheckoutError(data.checkout_error || null)
      setSubmitting(false)
    } catch {
      setError('Network error. Please check your connection and try again.')
      submitLockedRef.current = false
      setSubmitting(false)
    }
  }

  const handleCopyResumeUrl = async () => {
    if (!resumeUrl) return

    try {
      await navigator.clipboard.writeText(resumeUrl)
      setCopyStatus('Copied')
    } catch {
      setCopyStatus('Copy failed')
    }
  }

  const recoveryDelivery = recoveryEmailQueued ? (
    <div className="summary-screen__recovery-delivery" role="status" aria-live="polite">
      <span className="summary-screen__recovery-delivery-icon" aria-hidden="true">
        <svg viewBox="0 0 24 24" focusable="false">
          <path d="m5 12.5 4.2 4.2L19 7" />
        </svg>
      </span>
      <span>
        <strong>Recovery link sent</strong>
        <span>We emailed a secure 30-day link to {email.trim()}.</span>
      </span>
    </div>
  ) : resumeUrl ? (
    <div className="summary-screen__recovery-fallback">
      <BuilderAlert tone="warning" title="Save this recovery link">
        We couldn&apos;t queue the recovery email. Copy this link before leaving the page.
      </BuilderAlert>
      <div className="summary-screen__resume">
        <input type="text" readOnly value={resumeUrl} aria-label="Recovery link" />
        <button type="button" className="gs-btn-secondary" onClick={handleCopyResumeUrl}>
          Copy recovery link
        </button>
        {copyStatus && <span>{copyStatus}</span>}
      </div>
    </div>
  ) : null

  if (readOnlyRecovery) {
    const isDynamicQuote = pricingMode === 'per_square_foot'

    return (
      <div className="summary-screen" data-testid="read-only-recovery">
        <h2 className="summary-screen__title">
          {isDynamicQuote ? 'Your saved quote' : 'Your saved gang sheet'}
        </h2>

        {isDynamicQuote ? (
          <QuoteDetails quote={quote} testId="recovered-pricing-quote" totalOnly />
        ) : (
          <>
            <div className="summary-screen__summary">
              <p>{sheetSize?.label ? `${sheetSize.label} sheet` : 'Saved gang sheet'}</p>
            </div>
            {vendorManagedPayment && (
              <QuoteDetails quote={fixedOrderQuote} testId="recovered-fixed-order-quote" compact />
            )}
          </>
        )}

        <p className="summary-screen__handoff-message">
          {vendorManagedPayment
            ? 'This saved order has been sent to the print shop. They’ll confirm payment and fulfillment through their usual process.'
            : 'This recovery link shows the version that was saved. It is read-only so the original quote and design stay unchanged. Start a new gang sheet to make changes.'}
        </p>

        <div className="summary-screen__actions">
          {recoveryCheckoutUrl && (
            <button
              className="gs-btn-primary"
              type="button"
              onClick={() => redirectTo(recoveryCheckoutUrl)}
            >
              Continue to checkout
            </button>
          )}
          <a className="gs-btn-secondary" href={customerPortalHref} target="_top">View your gang sheets</a>
          <a className="gs-btn-secondary" href={`/builder/${vendorToken}`}>Start a new gang sheet</a>
        </div>
      </div>
    )
  }

  if (checkoutUrl) {
    return (
      <div className="summary-screen">
        <h2 className="summary-screen__title">Your gang sheet is saved</h2>
        {recoveryDelivery}

        <div className="summary-screen__portal">
          <p>Want to see all your saved gang sheets?</p>
          <a className="gs-btn-secondary" href={customerPortalHref} target="_top">
            View your gang sheets
          </a>
        </div>

        <div className="summary-screen__actions">
          <button
            className="gs-btn-primary"
            type="button"
            onClick={() => redirectTo(checkoutUrl)}
          >
            Continue to checkout
          </button>
        </div>
      </div>
    )
  }

  if (savedReferenceId && vendorManagedPayment) {
    return (
      <div className="summary-screen" data-testid="vendor-managed-order-saved">
        <h2 className="summary-screen__title">Your order request is saved</h2>
        <div className="summary-screen__summary">
          <p>GSPressHQ reference <strong>{savedReferenceId}</strong></p>
        </div>
        <QuoteDetails
          quote={pricingMode === 'per_square_foot' ? authoritativeQuote || quote : fixedOrderQuote}
          testId="saved-order-quote"
          compact={pricingMode !== 'per_square_foot'}
          totalOnly={pricingMode === 'per_square_foot'}
        />
        <p className="summary-screen__handoff-message">
          The print shop has received your gang sheet. They’ll confirm payment and fulfillment
          details through their usual process—no Shopify checkout is required.
        </p>
        {recoveryDelivery}
        <div className="summary-screen__actions">
          <a className="gs-btn-primary" href={customerPortalHref} target="_top">View your gang sheets</a>
          <a className="gs-btn-secondary" href={`/builder/${vendorToken}`}>Start a new gang sheet</a>
        </div>
      </div>
    )
  }

  if (savedReferenceId && pricingMode === 'per_square_foot') {
    return (
      <div className="summary-screen" data-testid="dynamic-quote-saved">
        <h2 className="summary-screen__title">Your quote is saved</h2>
        <QuoteDetails quote={authoritativeQuote || quote} testId="saved-pricing-quote" totalOnly />
        <p className="summary-screen__handoff-message">
          {checkoutError || (
            'Exact-price Shopify checkout is not configured for automatic-height sheets yet. Your server-calculated quote and design are saved for the vendor to review and fulfill.'
          )}
        </p>
        {recoveryDelivery}
        <div className="summary-screen__portal">
          <a className="gs-btn-secondary" href={customerPortalHref} target="_top">View your gang sheets</a>
        </div>
      </div>
    )
  }

  return (
    <div className="summary-screen">
      <h2 className="summary-screen__title">Almost Done</h2>

      <div className="summary-screen__summary">
        <p>
          {pricingMode === 'per_square_foot' ? 'Automatic-height sheet' : `${sheetSize.label} sheet`}
          {pricingMode !== 'per_square_foot' && (
            <>
              {physicalSheetCount > 1 && ` · ${physicalSheetCount}-sheet set`}
              {' · '}{quantity || 1} {(quantity || 1) === 1 ? 'copy' : 'copies'}
              {physicalSheetCount > 1 && ` · ${physicalSheetCount * (quantity || 1)} physical sheets`}
            </>
          )}
        </p>
      </div>

      {pricingMode === 'per_square_foot' && (
        <>
          <QuoteDetails quote={quote} testId="summary-pricing-quote" totalOnly />
          {quote?.billingUnit === 'sheet_variant' && quote?.sheets?.length > 0 && (
            <ul className="summary-screen__email-hint" data-testid="variant-sheet-lines">
              {quote.sheets.map(sheet => (
                <li key={sheet.sheet}>{`Sheet ${sheet.sheet}: ${quote.widthInches} × ${sheet.heightIn} in — $${(sheet.priceCents / 100).toFixed(2)}`}</li>
              ))}
            </ul>
          )}
          <p className="summary-screen__email-hint">
            {vendorManagedPayment
              ? 'This exact quote will be saved with the order for the print shop to collect.'
              : exactShopifyCheckoutAvailable
                ? quote?.billingUnit === 'sheet_variant'
                  ? `Each sheet is added to the shop's cart as its own item (${quote?.sheets?.length || 1} ${quote?.sheets?.length === 1 ? 'sheet' : 'sheets'}) so you can keep shopping.`
                  : 'Your exact quote will be saved in GSPressHQ and opened in Shopify checkout.'
                : 'This exact quote will be saved in GSPressHQ. Shopify exact-price checkout is not configured for this shop yet.'}
          </p>
        </>
      )}

      {vendorManagedPayment && pricingMode !== 'per_square_foot' && (
        <QuoteDetails quote={fixedOrderQuote} testId="summary-fixed-order-quote" compact />
      )}

      {vendorManagedPayment && (
        <p className="summary-screen__email-hint">
          This order will be sent directly to the print shop. They’ll handle payment through their
          invoice, website, POS, or existing process.
        </p>
      )}

      <div className="summary-screen__email">
        <label htmlFor="customer-email">Email address</label>
        <p className="summary-screen__email-hint">
          We&apos;ll use this to connect your design to your order.
        </p>
        <input
          id="customer-email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@example.com"
          disabled={submitting || Boolean(customerEmail)}
        />
      </div>

      <label className="summary-screen__artwork-rights">
        <input
          type="checkbox"
          checked={artworkRightsAccepted}
          onChange={(event) => setArtworkRightsAccepted(event.target.checked)}
          disabled={submitting}
        />
        <span>
          I agree to the{' '}
          <a href="/terms" target="_blank" rel="noopener noreferrer">GSPressHQ Terms of Service</a>,
          confirm that I own or have permission to reproduce all uploaded artwork, and authorize
          {' '}{vendorName || 'the print shop'} and GSPressHQ to process it for this order. I acknowledge
          the <a href="/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
          {' '}and <a href="/copyright" target="_blank" rel="noopener noreferrer">Copyright Policy</a>.
        </span>
      </label>

      {submitting && (
        <div className="summary-screen__submitting" role="status" aria-live="polite">
          Saving your gang sheet…
        </div>
      )}

      {(error || quoteBlockReason) && (
        <BuilderAlert className="summary-screen__error" tone="error" title="Couldn’t save your gang sheet">
          {error || quoteBlockReason}
        </BuilderAlert>
      )}

      <div className="summary-screen__actions">
        <button className="gs-btn-secondary" onClick={onBack} disabled={submitting}>
          ← Back
        </button>
        <button
          className="gs-btn-primary"
          onClick={handleSubmit}
          disabled={!isValidEmail || !artworkRightsAccepted || submitting || Boolean(quoteBlockReason)}
          aria-busy={submitting}
        >
          {submitting
            ? 'Saving…'
            : vendorManagedPayment
              ? 'Submit Order →'
              : pricingMode === 'per_square_foot'
                ? exactShopifyCheckoutAvailable
                  ? 'Save & Continue to Shopify →'
                  : 'Save Quote →'
                : 'Submit & Continue to Checkout →'}
        </button>
      </div>
    </div>
  )
}
